import { useState, useCallback, useMemo, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useAppStore } from '../../store/useAppStore'
import { ENV } from '../../shared/config/env'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import RecipeOfTheDay from '../../components/RecipeOfTheDay/RecipeOfTheDay'
import { StoriesTray } from '@widgets/stories'
import SectionTabs from '../../components/SectionTabs/SectionTabs'
import SearchBar from '../../components/SearchBar/SearchBar'
import CategoryGrid from '../../components/CategoryGrid/CategoryGrid'
import DebugPanel from '../../components/DebugPanel/DebugPanel'
import SubscriptionBlock from '../../components/SubscriptionBlock/SubscriptionBlock'
import './HomePage.css'

const HomePage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { searchQuery, hasActiveSubscription, setSearchQuery } = useAppStore()

  // Категории живут в URL и оттуда же читаются: отдельного состояния нет.
  // Раньше рядом жил useState с двусторонней синхронизацией — на «назад» эффект
  // успевал записать в адрес прежние, ещё не обновившиеся значения и возвращал
  // страницу обратно. Единственный источник правды это исключает.
  const categoryState = useMemo(
    () => ({
      section: searchParams.get('section') || '',
      subsection: searchParams.get('subsection') || '',
      subSubsection: searchParams.get('subSubsection') || '',
    }),
    [searchParams]
  )

  const [isFirstTabActive, setIsFirstTabActive] = useState(false)

  const hasSubscription = hasActiveSubscription()

  const hasActiveFilters = !!(
    categoryState.section ||
    categoryState.subsection ||
    categoryState.subSubsection
  )

  // Последнее применённое значение. CategoryGrid при клике по подкатегории
  // зовёт onSectionChange и onSubsectionChange подряд, в одном тике: второй
  // обработчик обязан видеть результат первого, а состояние из URL к этому
  // моменту ещё не обновилось. Раньше это держалось на функциональном
  // setState, теперь — на ссылке.
  const appliedRef = useRef(categoryState)
  appliedRef.current = categoryState

  const applyCategories = useCallback(
    (next: { section: string; subsection: string; subSubsection: string }) => {
      // Ничего не изменилось — не пишем запись в историю. CategoryGrid на клик
      // по категории верхнего уровня зовёт onSectionChange и onSubsectionChange
      // подряд, и второй вызов даёт тот же набор параметров: без этой проверки
      // в истории оказывались две одинаковые записи, и первое «назад»
      // перескакивало между ними, не меняя адрес.
      const current = appliedRef.current
      if (
        current.section === next.section &&
        current.subsection === next.subsection &&
        current.subSubsection === next.subSubsection
      ) {
        return
      }

      appliedRef.current = next

      const params = new URLSearchParams()
      if (next.section) params.set('section', next.section)
      if (next.subsection) params.set('subsection', next.subsection)
      if (next.subSubsection) params.set('subSubsection', next.subSubsection)

      // В вебе каждый уровень — отдельная запись истории: тогда кнопка «назад»
      // в браузере поднимает на уровень выше, а не уводит со страницы.
      // В Telegram оставляем replace: там возврат делает системная кнопка через
      // тот же обработчик, и лишние записи сломали бы ей стек и жест свайпа.
      setSearchParams(params, { replace: !ENV.IS_WEB })
    },
    [setSearchParams]
  )

  // Шаг на уровень вверх: подподкатегория → подкатегория → категория → главная.
  const handleBackButtonClick = useCallback(() => {
    const current = appliedRef.current
    if (current.subSubsection) {
      applyCategories({ ...current, subSubsection: '' })
    } else if (current.subsection) {
      applyCategories({ ...current, subsection: '', subSubsection: '' })
    } else if (current.section) {
      applyCategories({ section: '', subsection: '', subSubsection: '' })
    }
  }, [applyCategories])

  useTelegramBackButton(hasActiveFilters, handleBackButtonClick)

  const handleSectionChange = useCallback(
    (section: string) =>
      applyCategories({ section, subsection: '', subSubsection: '' }),
    [applyCategories]
  )

  const handleSubsectionChange = useCallback(
    (subsection: string) =>
      applyCategories({
        ...appliedRef.current,
        subsection,
        subSubsection: '',
      }),
    [applyCategories]
  )

  const handleSubSubsectionChange = useCallback(
    (subSubsection: string) =>
      applyCategories({ ...appliedRef.current, subSubsection }),
    [applyCategories]
  )

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const handleFirstTabStatusChange = (isFirstTab: boolean) => {
    setIsFirstTabActive(isFirstTab)
  }

  // Мемоизируем текущую активную категорию
  const currentSection = useMemo(() => {
    return (
      categoryState.subSubsection ||
      categoryState.subsection ||
      categoryState.section
    )
  }, [categoryState])

  return (
    <div className="homepage">
      {/* Баннер "Рецепт дня" */}
      <RecipeOfTheDay hasSubscription={hasSubscription} />

      {/* Лента историй */}
      <StoriesTray />

      {/* Блок подписки - только для пользователей без подписки */}

      {/* Фильтры-табы - только для пользователей с подпиской */}
      {hasSubscription && (
        <SectionTabs
          activeSection={categoryState.section}
          onSectionChange={handleSectionChange}
          activeSubsection={categoryState.subsection}
          onSubsectionChange={handleSubsectionChange}
          activeSubSubsection={categoryState.subSubsection}
          onSubSubsectionChange={handleSubSubsectionChange}
          onFirstTabStatusChange={handleFirstTabStatusChange}
        />
      )}

      {/* Поиск - только для пользователей с подпиской */}
      {hasSubscription && (
        <SearchBar
          placeholder="Поиск по карточкам"
          onSearch={handleSearch}
          isFirstTabActive={isFirstTabActive}
        />
      )}

      {/* Сетка категорий: подписчикам — полная, без подписки — превью-карточки */}
      <CategoryGrid
        section={currentSection}
        searchQuery={searchQuery}
        onSectionChange={handleSectionChange}
        onSubsectionChange={handleSubsectionChange}
        onSubSubsectionChange={handleSubSubsectionChange}
      />
      {!hasSubscription && (
        <SubscriptionBlock hasSubscription={hasSubscription} />
      )}

      {/* <DebugPanel /> */}
    </div>
  )
}

export default HomePage
